import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Building2,
  CalendarCheck,
  CalendarDays,
  CreditCard,
  FileText,
  GraduationCap,
  LayoutDashboard,
  Megaphone,
  Menu,
  Network,
  Receipt,
  Settings,
  ShieldCheck,
  Target,
  Upload,
  UserPlus,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";

import AdminPinGate from "../../components/AdminPinGate";
import { ViewAsToolbarButtons } from "../../components/GlobalViewAs";
import HubSwitcher from "../../components/HubSwitcher";
import SignOutConfirm from "../../components/SignOutConfirm";
import BrandLockup from "../../components/brand/BrandLockup";
import pmeReduced from "../../assets/brand/pme-reduced.svg";
import { useAuth } from "../../context/AuthContext";
import { useFeatures } from "../../context/FeaturesContext";
import { formatRole } from "../../utils/formatters";

type PermissionKey =
  | "can_manage_employees"
  | "can_view_compensation"
  | "can_edit_compensation"
  | "can_view_documents"
  | "can_manage_training"
  | "can_manage_leave"
  | "can_manage_onboarding"
  | "can_view_reports"
  | "can_manage_payroll"
  | "can_manage_certifications"
  | "can_manage_settings"
  | "can_manage_announcements"
  | "can_manage_expenses"
  | "can_manage_performance";

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  permission?: PermissionKey;
}

const NAV: NavItem[] = [
  { to: "/admin", labelKey: "admin.sidebar.dashboard", icon: LayoutDashboard, end: true },
  // People
  { to: "/admin/employees", labelKey: "admin.sidebar.employees", icon: Users },
  { to: "/admin/employees/bulk-upload", labelKey: "admin.sidebar.bulkUpload", icon: Upload },
  { to: "/admin/teams", labelKey: "admin.sidebar.teams", icon: Network, permission: "can_manage_employees" },
  // Time & Attendance
  { to: "/admin/reports", labelKey: "admin.sidebar.reports", icon: BarChart3, permission: "can_view_reports" },
  { to: "/admin/payroll", labelKey: "admin.sidebar.payroll", icon: CreditCard, permission: "can_manage_payroll" },
  // Leave
  { to: "/admin/leave", labelKey: "admin.sidebar.leave", icon: CalendarCheck, permission: "can_manage_leave" },
  { to: "/admin/calendar", labelKey: "admin.sidebar.calendar", icon: CalendarDays, permission: "can_manage_leave" },
  // Compliance
  { to: "/admin/training", labelKey: "admin.sidebar.training", icon: GraduationCap, permission: "can_manage_training" },
  { to: "/admin/compliance", labelKey: "admin.sidebar.compliance", icon: ShieldCheck, permission: "can_manage_certifications" },
  { to: "/admin/documents", labelKey: "admin.sidebar.documents", icon: FileText, permission: "can_view_documents" },
  // Performance
  { to: "/admin/performance", labelKey: "admin.sidebar.performance", icon: Target, permission: "can_manage_performance" },
  // Operations
  { to: "/admin/onboarding", labelKey: "admin.sidebar.onboarding", icon: UserPlus, permission: "can_manage_onboarding" },
  { to: "/admin/expenses", labelKey: "admin.sidebar.expenses", icon: Receipt, permission: "can_manage_expenses" },
  { to: "/admin/announcements", labelKey: "admin.sidebar.announcements", icon: Megaphone, permission: "can_manage_announcements" },
  // Config
  { to: "/admin/sites", labelKey: "admin.sidebar.sites", icon: Building2 },
  { to: "/admin/settings", labelKey: "admin.sidebar.settings", icon: Settings, permission: "can_manage_settings" },
];

function pageTitle(pathname: string, t: (k: string) => string): string {
  const match = NAV.find((n) =>
    n.end ? pathname === n.to : pathname.startsWith(n.to),
  );
  return match ? t(match.labelKey) : t("admin.panel");
}

export default function AdminLayout() {
  return (
    <AdminPinGate>
      <AdminLayoutInner />
    </AdminPinGate>
  );
}

/* Map admin nav paths to the feature flag that gates them */
const ADMIN_PATH_FEATURE_MAP: Record<string, string> = {
  "/admin/expenses": "expenses_enabled",
  "/admin/performance": "360_reviews_enabled",
};

function AdminLayoutInner() {
  const { t } = useTranslation();
  const { employee, hasPermission } = useAuth();
  const { isEnabled } = useFeatures();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNav = useMemo(
    () =>
      NAV.filter((item) => {
        if (item.permission && !hasPermission(item.permission)) return false;
        const flag = ADMIN_PATH_FEATURE_MAP[item.to];
        if (flag && !isEnabled(flag)) return false;
        return true;
      }),
    [hasPermission, isEnabled],
  );
  const title = pageTitle(location.pathname, t);

  return (
    <div className="flex min-h-dvh flex-col bg-pme-paper">
      <div className="flex flex-1">
      {/* ── Desktop full sidebar (lg+) ────────────────────────────── */}
      <aside className="hidden w-64 flex-shrink-0 flex-col bg-pme-navy lg:flex">
        <SidebarHeader t={t} />
        <BackToClock t={t} onClick={() => {}} />
        <FullNav items={visibleNav} t={t} onNavigate={() => {}} />
        <SidebarFooter employee={employee} t={t} />
      </aside>

      {/* ── Tablet icon-only sidebar (md–lg) ──────────────────────── */}
      <aside className="hidden w-[64px] flex-shrink-0 flex-col items-center bg-pme-navy py-3 md:flex lg:hidden">
        <img src={pmeReduced} alt="PME Cake" className="mb-3 h-7 w-auto" />
        <Link
          to="/home"
          title={t("nav.backToHome")}
          aria-label={t("nav.backToHome")}
          className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-pme-light-blue hover:bg-white/10"
        >
          <ArrowLeft size={20} strokeWidth={1.75} aria-hidden />
        </Link>
        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={t(item.labelKey)}
                aria-label={t(item.labelKey)}
                className={({ isActive }) =>
                  [
                    "flex h-10 w-10 items-center justify-center rounded-lg",
                    isActive
                      ? "bg-pme-blue text-white"
                      : "text-[#c7daea] hover:bg-white/10",
                  ].join(" ")
                }
              >
                <Icon size={20} strokeWidth={1.75} aria-hidden />
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* ── Mobile overlay sidebar (<md) ──────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <aside
            className="flex h-full w-[280px] flex-col bg-pme-navy shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <BrandLockup size="sm" reversed />
              <button
                type="button"
                aria-label={t("common.close")}
                onClick={() => setMobileOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-[#c7daea] hover:bg-white/10"
              >
                <X size={20} strokeWidth={1.75} aria-hidden />
              </button>
            </div>
            <BackToClock t={t} onClick={() => setMobileOpen(false)} />
            <FullNav items={visibleNav} t={t} onNavigate={() => setMobileOpen(false)} />
            <SidebarFooter employee={employee} t={t} />
          </aside>
        </div>
      )}

      {/* ── Main area ─────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar — always visible */}
        <header className="flex min-h-[56px] items-center gap-2 border-b sm:gap-3 border-gray-200 bg-white px-4">
          {/* Hamburger — mobile only */}
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 text-gray-600 md:hidden"
          >
            <Menu size={20} strokeWidth={1.75} aria-hidden />
          </button>

          {/* Page title */}
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
            {title}
          </p>

          {/* Hub switcher — scopes the whole admin panel to one hub */}
          {!!employee?.is_admin && <HubSwitcher />}

          {/* View as employee + preview controls — all admins */}
          {!!employee?.is_admin && <ViewAsToolbarButtons inline />}

          {/* User badge — tablet+ */}
          {employee && (
            <p className="hidden truncate text-xs text-gray-500 md:block">
              {employee.name} · {formatRole(employee.role)}
            </p>
          )}

          {/* Back to Clock — mobile + tablet (sidebar already has it on desktop) */}
          <Link
            to="/home"
            aria-label={t("nav.home")}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 sm:px-3 lg:hidden"
          >
            <ArrowLeft size={14} strokeWidth={2} aria-hidden />
            {/* Icon only on a phone, so the header fits beside the hub and view-as controls. */}
            <span className="hidden sm:inline">{t("nav.home")}</span>
          </Link>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────────────────

function SidebarHeader({ t }: { t: (k: string) => string }) {
  return (
    <div className="border-b border-white/10 px-5 py-4">
      <BrandLockup size="sm" reversed />
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-pme-light-blue">
        {t("admin.panel")}
      </p>
    </div>
  );
}

function BackToClock({
  t,
  onClick,
}: {
  t: (k: string) => string;
  onClick: () => void;
}) {
  return (
    <div className="px-3 py-2">
      <Link
        to="/home"
        onClick={onClick}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-pme-light-blue hover:bg-white/10"
      >
        <ArrowLeft size={14} strokeWidth={2} aria-hidden />
        {t("nav.backToHome")}
      </Link>
    </div>
  );
}

function FullNav({
  items,
  t,
  onNavigate,
}: {
  items: NavItem[];
  t: (k: string) => string;
  onNavigate: () => void;
}) {
  return (
    <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2 text-sm">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                "flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 font-medium",
                isActive
                  ? "bg-pme-blue text-white"
                  : "text-[#c7daea] hover:bg-white/10 hover:text-white",
              ].join(" ")
            }
          >
            <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0" />
            {t(item.labelKey)}
          </NavLink>
        );
      })}
    </nav>
  );
}

function SidebarFooter({
  employee,
  t,
}: {
  employee: { name: string; role: string } | null;
  t: (k: string) => string;
}) {
  const [showSignOut, setShowSignOut] = useState(false);
  return (
    <>
      {showSignOut && <SignOutConfirm onClose={() => setShowSignOut(false)} />}
      <div className="border-t border-white/10 px-4 py-3 text-xs text-[#c7daea]">
        {employee && (
          <>
            <p className="font-semibold text-white">{employee.name}</p>
            <p>{formatRole(employee.role as "employee")}</p>
          </>
        )}
        <div className="mt-3 flex flex-col gap-2">
          <Link
            to="/home"
            className="flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-white/10 px-3 text-sm font-semibold text-white hover:bg-white/20"
          >
            <ArrowLeft size={16} strokeWidth={2} aria-hidden />
            {t("nav.backToHome")}
          </Link>
          <button
            type="button"
            onClick={() => setShowSignOut(true)}
            className="min-h-[44px] text-center text-[#ffb4b4] hover:text-white hover:underline"
          >
            {t("auth.logout")}
          </button>
        </div>
      </div>
    </>
  );
}
